import { Button } from 'tio-ui/components/buttons';
import { concat, fn, hash } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import { v4 as uuid } from 'uuid';
import { Drawer } from 'tio-ui/components/overlays';
import { eq, and, or } from 'tio-ui/utilities';
import { Footer, Modal } from 'tio-ui/components/modal';
import { PhCheck, PhPencilSimple, PhPlus, PhTrash, PhPencil } from 'ember-phosphor-icons';
import { formatCamelCase } from 'tio-common/utils/format';
import { htmlSafe } from '@ember/template';
import { Input, Checkbox, Select, Form, RadioGroup } from 'tio-ui/components/forms';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { stateOptions } from 'tio-common/components/strategy-finder/strategy-finder-form';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { Divider } from 'tio-ui/components/utilities';
import { HStack, VStack, Section } from 'tio-ui/components/layout';
import { Text } from 'tio-ui/components/typography';
import * as v from 'valibot';
import Alert from 'tio-ui/components/alert';
import Component from '@glimmer/component';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import PslfField from 'tio-common/models/pslf-field';
import PslfForm from 'tio-common/models/pslf-form';
import PslfTemplate from 'tio-common/models/pslf-template';
import SignaturePad from 'tio-common/components/signature-pad';
import type { FormResultData } from 'tio-ui/components/forms';
import type { IntlService } from 'ember-intl';
import type { PointGroup } from 'signature_pad';
import type Employee from 'tio-common/models/employee';
import type FeaturesService from 'tio-common/services/features';
import type Owner from '@ember/owner';
import type PartnerService from 'tio-employee/services/partner';
import type PslfGroup from 'tio-common/models/pslf-group';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type { NotificationsService } from 'tio-ui/components/notifications';
import inputmask from 'tio-common/modifiers/inputmask';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import {
  batchCreateAndSignRequest,
  type BatchCreateAndSignResponse,
} from 'tio-common/requests/pslf-form';

interface S {
  Args: {
    template: PslfTemplate;
    employee: Employee;
  };
  Element: HTMLDivElement;
}

const boolean = Boolean;

type FormV2Data = {
  [key: string]: string | number | boolean | undefined;
};

interface PastEmploymentPeriod {
  id: string;
  pslfGroup?: PslfGroup;
  employmentStartDate: string;
  employmentEndDate: string;
  employmentHours: number;
  unknownEmployerFein?: boolean;
  manualEmployerName?: string;
}

// Schema for past period drawer form
const pastPeriodFormSchema = v.pipe(
  v.object({
    employmentStartDate: v.pipe(v.string(), v.nonEmpty('Start date is required.')),
    employmentEndDate: v.pipe(v.string(), v.nonEmpty('End date is required.')),
    employmentHours: v.pipe(
      v.number('Hours are required.'),
      v.minValue(1, 'Hours must be at least 1.'),
      v.maxValue(168, 'Hours cannot exceed 168.')
    ),
  }),
  v.forward(
    v.check((data) => {
      if (data.employmentStartDate && data.employmentEndDate) {
        const startDate = new Date(data.employmentStartDate);
        const endDate = new Date(data.employmentEndDate);
        return endDate > startDate;
      }
      return true;
    }, 'End date must be after start date.'),
    ['employmentEndDate']
  )
);

type PastPeriodFormData = {
  employmentStartDate: string;
  employmentEndDate: string;
  employmentHours: number | undefined;
};

const MAX_PAST_PERIODS = 6;

export default class PslfBorrowerForm extends Component<S> {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare features: FeaturesService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  @tracked hasMadeQualifyingPayments = false;
  @tracked signaturePoints: PointGroup[] = [];
  @tracked signatureImage?: string;
  @tracked selectedGroup?: PslfGroup;
  @tracked errors: Record<string, string[]> = {};
  @tracked signatureFieldErrors: string[] = [];
  @tracked currentOrPriorEmployer: 'currentEmployer' | 'priorEmployer' = 'currentEmployer';
  @tracked canSendElectronically = true;
  @tracked isPriorEmployerClosed = false;
  @tracked hasSubmitted = false;
  @tracked isOpenSignatureModal = false;
  @tracked isSsnFieldFocused = false;
  @tracked pastPeriods: PastEmploymentPeriod[] = [];
  @tracked pastPeriodErrors: Record<string, Record<string, string[]>> = {};
  @tracked isOpenPastPeriodDrawer = false;
  @tracked editingPeriodId?: string;
  @tracked editingPeriodGroup?: PslfGroup;
  @tracked editingPeriodGroupError: string[] = [];
  @tracked editingPeriodUnknownFein = false;
  @tracked editingPeriodManualEmployerName = '';
  @tracked drawerFormData: PastPeriodFormData = {
    employmentStartDate: '',
    employmentEndDate: '',
    employmentHours: undefined,
  };
  @tracked drawerFormErrors: Record<string, string[]> = {};
  @tracked hasDrawerSubmitted = false;

  constructor(owner: Owner, args: S['Args']) {
    super(owner, args);
    this.setDefaultGroup();
  }

  get defaultFein() {
    return this.args.employee.employeeAddition?.processed?.merged?.fein as string;
  }

  setDefaultGroup() {
    if (this.groupOptions.length === 1) {
      this.selectedGroup = this.groupOptions[0]?.item;
    } else if (this.defaultFein) {
      this.selectedGroup = this.args.employee.company.pslfGroups.find(
        (group: PslfGroup) =>
          group.employerFein.replace(/\D/g, '') === this.defaultFein.replace(/\D/g, '')
      );
    }
  }

  onSignatureUpdate = (points: PointGroup[], signatureUrl: string) => {
    this.signaturePoints = points;
    this.signatureImage = signatureUrl;

    if (signatureUrl) {
      this.signatureFieldErrors = [];
    }
  };

  closeSignatureModal = () => {
    this.signatureImage = undefined;
    this.signaturePoints = [];
    this.isOpenSignatureModal = false;
  };

  openSignatureModal = () => {
    this.isOpenSignatureModal = true;
  };

  captureSignature = () => {
    const hasValidSignature = !!this.signatureImage || !!this.args.employee.pslfProfile.signature;

    if (!hasValidSignature) {
      this.signatureFieldErrors = ['Signature is required.'];
      return;
    }

    const hasSignatureComplexity = this.signaturePoints.some((group) => group.points.length > 5);

    if (this.signatureImage && !hasSignatureComplexity) {
      this.signatureFieldErrors = ['Please provide a more detailed signature.'];
      return;
    }

    if (this.signatureImage) {
      this.args.employee.pslfProfile.signature = this.signatureImage;
    }

    this.signatureFieldErrors = [];
    this.isOpenSignatureModal = false;
  };

  get hasExistingSignature() {
    return !!this.args.employee.pslfProfile.signature;
  }

  get displaySignatureUrl() {
    return this.args.employee.pslfProfile.signature;
  }

  get maskedSsnValue() {
    const value = this.args.employee.pslfProfile.ssn || '';
    const digits = value.replace(/\D/g, '');

    // Show full SSN when editing/focused, masked when not
    if (this.isSsnFieldFocused || digits.length < 9) {
      return value;
    }

    // Mask first 5 digits (•••-••) and show last 4
    if (digits.length >= 9) {
      return `•••-••-${digits.slice(5, 9)}`;
    }
    return value;
  }

  onSsnFocus = () => {
    this.isSsnFieldFocused = true;
  };

  onSsnBlur = () => {
    this.isSsnFieldFocused = false;
  };

  onSsnChange = (value: string) => {
    this.args.employee.pslfProfile.setProperties({ ssn: value });
  };

  get states() {
    return stateOptions.map((state) => ({
      label: state.name,
      key: state.abbr,
    }));
  }

  borrowerStateValue = (field: PslfField) => {
    if (field.value?.length === 2) return field.value;
    return stateOptions.find((s) => s.name === field.value)?.abbr || '';
  };

  handleBorrowerStateChange = (field: PslfField, selectedKey: string) => {
    const newStateAbr = selectedKey || '';
    newStateAbr && this.setFieldValue(field, newStateAbr);
  };

  setFieldValue = (field: PslfField, value: string | boolean | number) => {
    // Set all fields with the same key
    this.args.template.pslfFields.forEach((f) => {
      if (f.key === field.key) {
        f.setProperties({ value });
      }
    });
  };

  buildFieldSchema = (field: PslfField) => {
    const { fieldType, required } = field;
    const requiredMessage = `${field.label} is required.`;
    // @ts-expect-error valibot types
    let baseSchema: v.BaseSchema;
    switch (fieldType) {
      case 'checkbox':
        baseSchema = this.buildCheckboxSchema(field, requiredMessage);
        break;
      case 'email':
        baseSchema = this.buildEmailSchema(requiredMessage);
        break;
      case 'date':
        baseSchema = this.buildDateSchema(field, requiredMessage);
        break;
      case 'number':
        baseSchema = this.buildNumberSchema(field, requiredMessage);
        break;
      case 'text':
        baseSchema = this.buildTextSchema(field, requiredMessage);
        break;
      case 'phone':
        baseSchema = this.buildPhoneSchema(field, requiredMessage);
        break;
      default:
        baseSchema = v.any();
        break;
    }
    if (!required) {
      const defaultValue = fieldType === 'number' || fieldType === 'date' ? undefined : '';
      return v.optional(v.nullable(baseSchema, defaultValue));
    }
    return baseSchema;
  };

  private buildCheckboxSchema = (field: PslfField, requiredMessage: string) => {
    return field.required ? v.literal(true, requiredMessage) : v.boolean();
  };

  private buildEmailSchema = (requiredMessage: string) => {
    return v.pipe(
      v.string(),
      v.nonEmpty(requiredMessage),
      v.email('Please enter a valid email address.')
    );
  };

  private buildDateSchema = (field: PslfField, requiredMessage: string) => {
    const dateTransform = (input: Date) => {
      if (!input || !(input instanceof Date) || isNaN(input.getTime())) {
        return input;
      }
      const year = input.getUTCFullYear();
      if (year < 1900 || year > 2100) {
        return input;
      }
      const month = (input.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = input.getUTCDate().toString().padStart(2, '0');
      const sanitized = `${year}-${month}-${day}`;
      this.setFieldValue(field, sanitized);
      return input;
    };

    if (field.key === 'borrowerBirthDate') {
      const minDate = new Date('1900-01-01');
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() - 18);
      return v.pipe(
        v.date(requiredMessage),
        v.minValue(minDate, 'Date of birth cannot be before 1900.'),
        v.maxValue(maxDate, 'You must be at least 18 years old.'),
        v.transform(dateTransform)
      );
    }

    return v.pipe(
      v.date(requiredMessage),
      v.check((input) => (field.required ? !!input : true), requiredMessage),
      v.transform(dateTransform)
    );
  };

  private buildNumberSchema = (field: PslfField, requiredMessage: string) => {
    return v.pipe(
      v.number(requiredMessage),
      v.minValue(1, `${field.label} must be greater than 0.`)
    );
  };

  private buildTextSchema = (field: PslfField, requiredMessage: string) => {
    switch (field.key) {
      case 'ssn':
        return this.buildSsnSchema(requiredMessage);
      case 'borrowerZip':
        return this.buildZipSchema(requiredMessage);
      case 'employerFein':
        return this.buildFeinSchema(requiredMessage);
      default:
        return this.buildGenericTextSchema(field, requiredMessage);
    }
  };

  private buildSsnSchema = (requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.nonEmpty(requiredMessage),
      v.regex(/^(\d{3}-\d{2}-\d{4}|•••-••-\d{4})$/, 'Please enter a valid social security number')
    );
  };

  private buildZipSchema = (requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.nonEmpty(requiredMessage),
      v.regex(/^\d{5}$/, 'Please enter a valid 5-digit ZIP code.')
    );
  };

  private buildFeinSchema = (requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.nonEmpty(requiredMessage),
      v.transform((input) => {
        let sanitized = input.replace(/\D/g, '');
        if (sanitized.length > 2) {
          sanitized = sanitized.slice(0, 2) + '-' + sanitized.slice(2, 9);
        }
        return sanitized;
      }),
      v.regex(/^\d{2}-?\d{7}$/, 'FEIN must be 9 digits')
    );
  };

  private buildGenericTextSchema = (field: PslfField, requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.check((input) => (field.required ? !!input.length : true), requiredMessage)
    );
  };

  private buildPhoneSchema = (field: PslfField, requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.transform((val) => {
        if (!val || val.length === 0) return '';
        const digits = val.replace(/\D/g, '');
        if (digits.length === 11 && digits.startsWith('1')) {
          return `+${digits}`;
        }
        if (val.startsWith('+') && digits.length === 11 && digits.startsWith('1')) {
          return val;
        }
        return val;
      }),
      v.check((input) => {
        if (!field.required && input.length === 0) return true;
        return /^\+1\d{10}$/.test(input);
      }, 'Please enter a valid US phone number.')
    );
  };

  get formSchema() {
    const allFields: PslfField[] = [
      ...this.args.template.borrowerFormFields.personalInfo,
      ...this.args.template.borrowerFormFields.address,
      ...this.args.template.borrowerFormFields.employerFields,
    ].filter((field: PslfField) => this.isFieldVisible(field.key));
    const schemaDefinition = Object.fromEntries(
      allFields.map((field: PslfField) => [field.key, this.buildFieldSchema(field)])
    );

    return v.object(schemaDefinition);
  }

  validateFields = (data: FormV2Data) => {
    const result = v.safeParse(this.formSchema, data);

    if (result.success) {
      this.errors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};

      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.errors = validationErrors;
    }

    const profileSsn = this.args.employee.pslfProfile.ssn || '';
    const ssnDigits = profileSsn.replace(/\D/g, '');
    const isValidSsn = ssnDigits.length === 9;
    if (!isValidSsn) {
      this.errors = {
        ...this.errors,
        ssn: [...(this.errors.ssn || []), 'Please enter a valid social security number'],
      };
    }

    if (this.currentOrPriorEmployer === 'currentEmployer' && !this.selectedGroup) {
      this.errors = { employmentGroup: ['Please select your employer group.'], ...this.errors };
    } else {
      delete this.errors.employmentGroup;
    }
  };

  onFormChange = (result: FormResultData<FormV2Data>) => {
    this.validateFields(result.data);
    Object.entries(result.data).forEach(([name, value]) => {
      const field = this.args.template.pslfFields.find((f) => f.key === name);
      if (field) {
        this.setFieldValue(field, value || '');
      }
    });
  };

  onFormSubmit = (result: FormResultData<FormV2Data>) => {
    this.hasSubmitted = true;
    this.validateFields(result.data);

    // Validate past periods if any exist
    const pastPeriodsValid = this.pastPeriods.length === 0 || this.validatePastPeriods();

    if (!this.hasExistingSignature) {
      this.signatureFieldErrors = ['Please provide a signature before submitting.'];
    } else {
      this.signatureFieldErrors = [];
    }

    const allErrors = {
      ...this.errors,
      ...(this.signatureFieldErrors.length > 0 ? { signature: this.signatureFieldErrors } : {}),
    };

    if (Object.keys(allErrors).length === 0 && pastPeriodsValid) {
      this.onSubmit.perform();
    } else {
      // Focus the first error
      const firstErrorKey = Object.keys(allErrors)[0];
      const el = document.querySelector(`[name='${firstErrorKey}']`);
      if (el && 'focus' in el) {
        (el as HTMLElement).focus();
      }
    }
  };

  private buildFieldsObject = (
    keyPrefixes: string[]
  ): { [k: string]: string | boolean | number } => {
    return Object.fromEntries(
      this.args.template.pslfFields
        .filter((field) => {
          const matchesPrefix = keyPrefixes.some((prefix) => field.key.startsWith(prefix));
          return (
            matchesPrefix &&
            field.fieldType !== 'signature' &&
            field.value &&
            this.isFieldVisible(field.key)
          );
        })
        .map((field) => [field.key, field.value])
    );
  };

  onSubmit = dropTask(async () => {
    const borrowerFields = this.buildFieldsObject(['borrower']);
    const signDate = new Date().toISOString().split('T')[0] || ''; // YYYY-MM-DD
    borrowerFields['borrowerNonPartnerForm'] = this.isNonPartnerOrPriorEmployer;
    borrowerFields['borrowerSignDate'] = signDate;
    borrowerFields['borrowerPriorEmployerClosed'] = this.isPriorEmployerClosed;
    borrowerFields['borrowerPrintAndMail'] =
      !this.canSendElectronically || this.isPriorEmployerClosed;

    const employerFields = this.buildFieldsObject(['employer', 'employment']);
    employerFields['employmentContinues'] =
      !this.isNonPartnerOrPriorEmployer && !employerFields['employmentEndDate'];
    if (!this.isNonPartnerOrPriorEmployer) {
      employerFields['employmentStatus'] = 'fullTime';
    }

    // Determine if pre-approved
    const hours = employerFields['employmentHours'] as number;
    const employeeAdditionHours = Number(
      this.args.employee.employeeAddition?.processed?.merged?.['hoursWorkedPerWeek']
    );
    // Only care about hours changing if not full-time
    const isFullTime = employeeAdditionHours >= 30 && hours >= 30;
    const isHoursChanged =
      Number.isNaN(employeeAdditionHours) ||
      (!isFullTime && Math.abs(hours - employeeAdditionHours) > 0.5);
    const isStartDateChanged =
      employerFields['employmentStartDate'] !==
      this.args.employee.employeeAddition?.processed?.merged?.['employmentStartDate'];
    const isPreApproved =
      !!this.selectedGroup?.preApprovedTemplateId &&
      !isHoursChanged &&
      hours >= 30 &&
      !isStartDateChanged;
    if (isPreApproved) {
      employerFields['employerSignDate'] = signDate;
    }

    try {
      if (this.args.employee.pslfProfile.hasDirtyAttributes) {
        await this.args.employee.pslfProfile.save();
      }

      if (this.pastPeriods.length > 0 && this.currentOrPriorEmployer === 'currentEmployer') {
        const { content: result } = (await this.store.request(
          batchCreateAndSignRequest({
            pslfProfileId: this.args.employee.pslfProfile.id!,
            pslfTemplateId: this.args.template.id!,
            primaryForm: {
              formType: this.currentOrPriorEmployer,
              pslfGroupId: this.isNonPartnerOrPriorEmployer ? undefined : this.selectedGroup?.id,
              isNonPartner: this.isNonPartnerOrPriorEmployer,
              isPreApproved,
              borrowerFields,
              employerFields,
            },
            pastPeriods: this.pastPeriods.map((period) => ({
              pslfGroupId: period.unknownEmployerFein
                ? undefined
                : (period.pslfGroup?.id ?? undefined),
              unknownEmployerFein: period.unknownEmployerFein,
              employerFields: {
                employmentStartDate: period.employmentStartDate,
                employmentEndDate: period.employmentEndDate,
                employmentHours: period.employmentHours,
                employerName: period.manualEmployerName,
              },
            })),
          })
        )) as { content: BatchCreateAndSignResponse };

        this.router.replaceWith('authenticated.pslf.form.success', result.primaryFormId, {
          queryParams: {
            pastPeriodIds: result.pastPeriodFormIds.length
              ? result.pastPeriodFormIds.join(',')
              : undefined,
          },
        });
      } else {
        const pslfGroup = this.isNonPartnerOrPriorEmployer ? undefined : this.selectedGroup;

        const formRecord = this.store.createRecord<PslfForm>('pslf-form', {
          formType: this.currentOrPriorEmployer,
          pslfProfile: this.args.employee.pslfProfile,
          pslfGroup,
          isNonPartner: this.isNonPartnerOrPriorEmployer,
          // @ts-expect-error: API fills in the rest of the fields
          borrowerFields,
          // @ts-expect-error: API fills in the rest of the fields
          employerFields,
        });

        formRecord.pslfTemplate = this.args.template;
        formRecord.isPreApproved = isPreApproved;

        await formRecord.save({ adapterOptions: { useV2: true } });

        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        const res = await this.store.adapterFor('pslf-form').signBorrower(formRecord);

        this.router.replaceWith('authenticated.pslf.form.success', res.id);
      }
    } catch (error) {
      console.error('Error submitting PSLF form ', error);
      const errorMessage = this.intl.t('pslf.start_new.form_submitted_error');
      this.notifications.add(errorMessage, { appearance: 'error' });
    }
  });

  get groupOptions() {
    return this.args.employee.company.pslfGroups.map((group: PslfGroup) => ({
      ...group,
      item: group,
      id: group.id,
      label: `${group.employerCommonName ? group.employerCommonName + ' - ' : ''}${
        group.employerFein
      }`,
    }));
  }

  selectGroup = (groupId: string) => {
    this.selectedGroup = this.args.employee.company.pslfGroups.find(
      (group: PslfGroup) => group.id === groupId
    );
  };

  // Past period drawer management methods
  openAddPeriodDrawer = () => {
    if (this.pastPeriods.length >= MAX_PAST_PERIODS) return;
    this.editingPeriodId = uuid();
    this.editingPeriodGroup = undefined;
    this.editingPeriodGroupError = [];
    this.editingPeriodUnknownFein = false;
    this.editingPeriodManualEmployerName = '';
    this.drawerFormData = {
      employmentStartDate: '',
      employmentEndDate: '',
      employmentHours: undefined,
    };
    this.isOpenPastPeriodDrawer = true;
  };

  openEditPeriodDrawer = (period: PastEmploymentPeriod) => {
    this.editingPeriodId = period.id;
    this.editingPeriodGroup = period.pslfGroup;
    this.editingPeriodGroupError = [];
    this.editingPeriodUnknownFein = period.unknownEmployerFein ?? false;
    this.editingPeriodManualEmployerName = period.manualEmployerName ?? '';
    this.drawerFormData = {
      employmentStartDate: period.employmentStartDate || '',
      employmentEndDate: period.employmentEndDate || '',
      employmentHours: period.employmentHours || 0,
    };
    this.isOpenPastPeriodDrawer = true;
  };

  closePastPeriodDrawer = () => {
    this.drawerFormData = {
      employmentStartDate: '',
      employmentEndDate: '',
      employmentHours: undefined,
    };
    this.drawerFormErrors = {};
    this.hasDrawerSubmitted = false;
    this.isOpenPastPeriodDrawer = false;
    this.editingPeriodId = undefined;
    this.editingPeriodGroup = undefined;
    this.editingPeriodGroupError = [];
    this.editingPeriodUnknownFein = false;
    this.editingPeriodManualEmployerName = '';
  };

  removePastPeriod = (id: string) => {
    this.pastPeriods = this.pastPeriods.filter((p) => p.id !== id);
    // Remove errors for this period
    const newErrors = { ...this.pastPeriodErrors };
    delete newErrors[id];
    this.pastPeriodErrors = newErrors;
  };

  selectDrawerGroup = (groupId: string) => {
    const group = this.args.employee.company.pslfGroups.find((g: PslfGroup) => g.id === groupId);
    this.editingPeriodGroup = group;
    this.editingPeriodGroupError = [];
    this.editingPeriodUnknownFein = false;
    this.editingPeriodManualEmployerName = '';
  };

  handleDrawerFormChange = (result: FormResultData<PastPeriodFormData>) => {
    this.drawerFormData = { ...this.drawerFormData, ...result.data };

    // Validate and update errors if user has already attempted to submit
    if (this.hasDrawerSubmitted) {
      this.validateDrawerForm();
    }
  };

  validateDrawerForm = (): v.InferOutput<typeof pastPeriodFormSchema> | null => {
    const validationResult = v.safeParse(pastPeriodFormSchema, this.drawerFormData);

    if (validationResult.success) {
      this.drawerFormErrors = {};
      return validationResult.output;
    } else {
      const validationErrors: Record<string, string[]> = {};
      for (const issue of validationResult.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.drawerFormErrors = validationErrors;
      return null;
    }
  };

  handleDrawerStartDateChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    this.drawerFormData = {
      ...this.drawerFormData,
      employmentStartDate: date ? (date.toISOString().split('T')[0] ?? '') : '',
    };

    // Re-validate if user has already submitted
    if (this.hasDrawerSubmitted) {
      this.validateDrawerForm();
    }
  };

  handleDrawerEndDateChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    this.drawerFormData = {
      ...this.drawerFormData,
      employmentEndDate: date ? (date.toISOString().split('T')[0] ?? '') : '',
    };

    // Re-validate if user has already submitted
    if (this.hasDrawerSubmitted) {
      this.validateDrawerForm();
    }
  };

  toggleUnknownFein = () => {
    this.editingPeriodUnknownFein = !this.editingPeriodUnknownFein;
    // Clear group selection and error when toggling to unknown FEIN
    if (this.editingPeriodUnknownFein) {
      this.editingPeriodGroup = undefined;
      this.editingPeriodGroupError = [];
    }
  };

  setManualEmployerName = (value: string) => {
    this.editingPeriodManualEmployerName = value;
  };

  handleDrawerFormSubmit = () => {
    this.hasDrawerSubmitted = true;

    // Validate group separately since it's not part of the form schema
    // Group is only required when unknownEmployerFein is false
    if (!this.editingPeriodUnknownFein && !this.editingPeriodGroup) {
      this.editingPeriodGroupError = [
        String(this.intl.t('pslf.start_new.errors.select_employer_group')),
      ];
    } else {
      this.editingPeriodGroupError = [];
    }

    const parsedData = this.validateDrawerForm();

    if (!parsedData || this.editingPeriodGroupError.length > 0) {
      return;
    }

    if (!this.editingPeriodId) return;

    const newPeriod: PastEmploymentPeriod = {
      id: this.editingPeriodId,
      pslfGroup: this.editingPeriodUnknownFein ? undefined : this.editingPeriodGroup,
      employmentStartDate: parsedData.employmentStartDate,
      employmentEndDate: parsedData.employmentEndDate,
      employmentHours: parsedData.employmentHours,
      unknownEmployerFein: this.editingPeriodUnknownFein,
      manualEmployerName: this.editingPeriodUnknownFein
        ? this.editingPeriodManualEmployerName
        : undefined,
    };

    const existingIndex = this.pastPeriods.findIndex((p) => p.id === this.editingPeriodId);

    if (existingIndex >= 0) {
      this.pastPeriods = this.pastPeriods.map((p) =>
        p.id === this.editingPeriodId ? newPeriod : p
      );
    } else {
      this.pastPeriods = [...this.pastPeriods, newPeriod];
    }

    this.closePastPeriodDrawer();
  };

  get fullPastPeriodSchema() {
    return v.pipe(
      v.object({
        id: v.string(),
        unknownEmployerFein: v.optional(v.boolean()),
        manualEmployerName: v.optional(v.string()),
        pslfGroup: v.pipe(
          v.any(),
          // Group is only required when unknownEmployerFein is false
          // This check is handled separately per-period in validatePastPeriods
          v.check(() => true, 'Please select an employer group.')
        ),
        employmentStartDate: v.pipe(
          v.string('Start date is required.'),
          v.nonEmpty('Start date is required.')
        ),
        employmentEndDate: v.pipe(
          v.string('End date is required.'),
          v.nonEmpty('End date is required.')
        ),
        employmentHours: v.pipe(
          v.number('Hours are required.'),
          v.minValue(1, 'Hours must be at least 1.'),
          v.maxValue(168, 'Hours cannot exceed 168.')
        ),
      }),
      v.forward(
        v.check((data) => {
          if (data.employmentStartDate && data.employmentEndDate) {
            const start = new Date(data.employmentStartDate);
            const end = new Date(data.employmentEndDate);
            return end > start;
          }
          return true;
        }, 'End date must be after start date.'),
        ['employmentEndDate']
      ),
      // Validate pslfGroup is required only when unknownEmployerFein is false
      v.forward(
        v.check((data) => {
          if (data.unknownEmployerFein) {
            return true; // Group not required when FEIN is unknown
          }
          return !!data.pslfGroup;
        }, 'Please select an employer group.'),
        ['pslfGroup']
      )
    );
  }

  validatePastPeriods = () => {
    const errors: Record<string, Record<string, string[]>> = {};
    let isValid = true;

    for (const period of this.pastPeriods) {
      const result = v.safeParse(this.fullPastPeriodSchema, period);

      if (!result.success) {
        const periodErrors: Record<string, string[]> = {};

        for (const issue of result.issues) {
          const path = issue.path?.[0]?.key as string;
          if (path) {
            if (!periodErrors[path]) {
              periodErrors[path] = [];
            }
            periodErrors[path].push(issue.message);
          }
        }

        if (Object.keys(periodErrors).length > 0) {
          errors[period.id] = periodErrors;
          isValid = false;
        }
      }
    }

    this.pastPeriodErrors = errors;
    return isValid;
  };

  get showPastPeriodsSection() {
    // TODO: how to handle non-partner employers?
    return (
      this.features.isEnabled('pslfPastPeriods') &&
      this.currentOrPriorEmployer === 'currentEmployer' &&
      !this.isNonPartnerEmployer
    );
  }

  get canAddMorePeriods() {
    return this.pastPeriods.length < MAX_PAST_PERIODS;
  }

  get isEditingExistingPeriod() {
    return this.pastPeriods.some((p) => p.id === this.editingPeriodId);
  }

  get showGroupSelect() {
    return (
      !this.isPriorEmployer &&
      !this.isNonPartnerEmployer &&
      this.groupOptions.length > 1 &&
      !this.defaultFein
    );
  }

  get isNonPartnerEmployer() {
    const employerPslfModule = this.partner.settingModules?.pslf;
    return !employerPslfModule || employerPslfModule.isNonPartnerEmployer;
  }

  get isPriorEmployer() {
    return this.currentOrPriorEmployer === 'priorEmployer';
  }

  get isNonPartnerOrPriorEmployer() {
    return this.isNonPartnerEmployer || this.isPriorEmployer;
  }

  get isEmployerContactFieldsVisible() {
    return this.isNonPartnerOrPriorEmployer && !this.isPriorEmployerClosed;
  }

  isFieldVisible = (fieldKey: string): boolean => {
    switch (fieldKey) {
      case 'employerName':
        return this.isNonPartnerOrPriorEmployer;
      case 'employerFein':
        return this.isNonPartnerOrPriorEmployer && this.isPriorEmployerClosed;
      case 'employerWebsite':
      case 'employerContactName':
      case 'employerContactPhoneNumber':
      case 'employerContactTitle':
        return this.isEmployerContactFieldsVisible;
      case 'employerContactEmail':
        return this.isEmployerContactFieldsVisible && this.canSendElectronically;
      case 'employmentEndDate':
        return this.isPriorEmployer;
      default:
        return true;
    }
  };

  <template>
    <Form @onChange={{this.onFormChange}} @onSubmit={{this.onFormSubmit}} data-test="pslf-form">
      <VStack>
        <Section class="border-0">
          <:header>{{t "pslf.start_new.pslf_form_header"}}</:header>
          <:body>
            {{t "pslf.start_new.pslf_preamble"}}
          </:body>
        </Section>
        {{#each-in @template.borrowerFormFields as |formGroup fields|}}
          <Section class="border-0">
            <:header>
              {{#if (eq formGroup "personalInfo")}}
                {{t "pslf.start_new.form_section_personal_info"}}
              {{else if (eq formGroup "address")}}
                {{t "pslf.start_new.form_section_address"}}
              {{else if (eq formGroup "employerFields")}}
                {{t "pslf.start_new.form_section_employer_fields"}}
              {{else}}
                {{formatCamelCase formGroup}}
              {{/if}}
            </:header>
            <:body>
              <VStack>
                <div><span class="text-danger font-semibold mr-1">*</span>{{t
                    "pslf.start_new.required_items"
                  }}</div>
                {{#if (eq formGroup "employerFields")}}
                  <RadioGroup
                    @label={{t "pslf.start_new.select_employer"}}
                    @onChange={{fn (mut this.currentOrPriorEmployer)}}
                    @value={{this.currentOrPriorEmployer}}
                    data-test="radio-select-employer"
                    as |Radio|
                  >
                    <VStack>
                      <Radio @value="currentEmployer" @label={{t "pslf.start_new.current"}} />
                      <Radio @value="priorEmployer" @label={{t "pslf.start_new.former"}} />
                    </VStack>
                  </RadioGroup>
                  {{#if this.showGroupSelect}}
                    <Select
                      @name="employmentGroup"
                      @label="Select Employer"
                      @items={{this.groupOptions}}
                      @onAction={{this.selectGroup}}
                      data-test="select-employer"
                      @errors={{errorsForValibotField
                        "employmentGroup"
                        schemaErrors=this.errors
                        hasSubmitted=this.hasSubmitted
                      }}
                    />
                  {{/if}}
                {{/if}}
                {{#each fields as |field|}}
                  {{#if (eq field.fieldType "checkbox")}}
                    {{#if (eq field.key "borrowerWantsForbearance")}}
                      <RadioGroup
                        @label={{t "pslf.workflow.reason_submitting.have_you_made"}}
                        @value={{this.hasMadeQualifyingPayments}}
                        @onChange={{fn (mut this.hasMadeQualifyingPayments)}}
                        data-test="radio-borrower-wants-forbearance"
                        as |Radio|
                      >
                        <VStack>
                          <Radio @label={{t "common.no"}} @value={{false}} data-test="radio-no" />
                          <Radio @label={{t "common.yes"}} @value={{true}} data-test="radio-yes" />
                        </VStack>
                      </RadioGroup>
                      {{#if this.hasMadeQualifyingPayments}}
                        <p>
                          {{t "pslf.workflow.reason_submitting.forbearance.tooltip"}}
                        </p>
                        <Checkbox
                          @name={{field.key}}
                          {{! @glint-expect-error: label arg doesn't like SafeString}}
                          @label={{htmlSafe
                            (t "pslf.workflow.reason_submitting.forbearance.checkbox")
                          }}
                          @checked={{boolean field.value}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          data-test="checkbox-forbearance"
                        />
                      {{/if}}
                    {{else if
                      (and (eq field.key "borrowerPriorEmployerClosed") this.isPriorEmployer)
                    }}
                      <RadioGroup
                        @label={{field.label}}
                        @onChange={{fn (mut this.isPriorEmployerClosed)}}
                        @value={{this.isPriorEmployerClosed}}
                        data-test="radio-select-partner-closed-or-refused"
                        as |Radio|
                      >
                        <VStack>
                          <Radio @value={{false}} @label={{t "common.no"}} />
                          <Radio @value={{true}} @label={{t "common.yes"}} />
                        </VStack>
                      </RadioGroup>
                      {{#if this.isPriorEmployerClosed}}
                        <Alert>{{htmlSafe (t "pslf.workflow.print_and_mail_alert")}}</Alert>
                      {{/if}}
                    {{/if}}
                  {{else if (eq field.key "borrowerState")}}
                    <Select
                      @name={{field.key}}
                      @selectionMode="single"
                      @onAction={{fn this.handleBorrowerStateChange field}}
                      @items={{this.states}}
                      @selectedKey={{this.borrowerStateValue field}}
                      @isRequired={{field.required}}
                      @label={{field.label}}
                      @allowEmpty={{true}}
                      @isFilterable={{true}}
                      @errors={{errorsForValibotField
                        field.key
                        schemaErrors=this.errors
                        hasSubmitted=this.hasSubmitted
                      }}
                      @placeholder="Select a state"
                      data-test="select-borrower-state"
                    />
                  {{else}}
                    {{#if
                      (and
                        (eq field.key "employerContactEmail") this.isEmployerContactFieldsVisible
                      )
                    }}
                      <RadioGroup
                        @label={{t "pslf.workflow_prior_employer.send_electronically"}}
                        @onChange={{fn (mut this.canSendElectronically)}}
                        @value={{this.canSendElectronically}}
                        data-test="radio-select-can-send-electronically"
                        as |Radio|
                      >
                        <VStack>
                          <Radio @value={{false}} @label={{t "pslf.workflow.no_employer_email"}} />
                          <Radio @value={{true}} @label={{t "pslf.workflow.yes_employer_email"}} />
                        </VStack>
                      </RadioGroup>
                      {{#if this.canSendElectronically}}
                        <p>{{t "pslf.workflow.please_provide"}}</p>
                      {{else}}
                        <Alert>{{htmlSafe (t "pslf.workflow.print_and_mail_alert")}}</Alert>
                      {{/if}}
                    {{/if}}
                    {{#if (this.isFieldVisible field.key)}}
                      {{#if (eq field.key "ssn")}}
                        <Input
                          @name={{field.key}}
                          @type="text"
                          @label={{field.label}}
                          @value={{this.maskedSsnValue}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          placeholder="###-##-####"
                          @onInput={{this.onSsnChange}}
                          @isRequired={{true}}
                          {{on "focus" this.onSsnFocus}}
                          {{on "blur" this.onSsnBlur}}
                          {{inputmask
                            mask="MMM-MM-9999"
                            definitions=(hash M=(hash validator="[0-9•]"))
                          }}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else if (eq field.key "borrowerZip")}}
                        <Input
                          @name={{field.key}}
                          @type="text"
                          @label={{field.label}}
                          @value={{field.value}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          placeholder="#####"
                          {{inputmask mask="99999"}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else if (eq field.key "employerFein")}}
                        <Input
                          @name={{field.key}}
                          @type="text"
                          @label={{field.label}}
                          @value={{field.value}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          placeholder="##-#######"
                          {{inputmask mask="99-9999999"}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else if (eq field.fieldType "phone")}}
                        <Input
                          @name={{field.key}}
                          @type="tel"
                          @label={{field.label}}
                          @value={{field.value}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          placeholder="+1 (###) ###-####"
                          {{inputmask mask="+1 (999) 999-9999"}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else}}
                        <Input
                          @name={{field.key}}
                          @type={{field.fieldType}}
                          @label={{field.label}}
                          @value={{field.value}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{/if}}
                    {{/if}}
                  {{/if}}
                {{/each}}
              </VStack>
            </:body>
          </Section>
        {{/each-in}}
        {{#if this.showPastPeriodsSection}}
          <Section class="border-0" data-test="past-periods-section">
            <:header>{{t "pslf.start_new.past_employment_periods"}}</:header>
            <:actions>
              {{#if this.canAddMorePeriods}}
                <Button
                  @intent="primary"
                  @appearance="minimal"
                  {{on "click" this.openAddPeriodDrawer}}
                  data-test="btn-add-past-period"
                >
                  <PhPlus @size={{24}} class="mr-2" />
                  {{t "pslf.start_new.add_past_period"}}
                </Button>
              {{/if}}
            </:actions>
            <:body>
              <VStack>
                {{#unless this.pastPeriods.length}}
                  <Text @intent="muted" @tag="p">
                    {{t "pslf.start_new.no_past_periods"}}
                  </Text>
                {{/unless}}
                {{#each this.pastPeriods as |period index|}}
                  <Section>
                    <:header>
                      {{#if period.unknownEmployerFein}}
                        {{#if period.manualEmployerName}}
                          {{period.manualEmployerName}}
                          -
                        {{/if}}
                        <span class="text-amber-600">{{t
                            "pslf.start_new.unknown_fein_label"
                          }}</span>
                      {{else}}
                        {{period.pslfGroup.employerCommonName}}
                        -
                        {{period.pslfGroup.employerFein}}
                      {{/if}}
                    </:header>
                    <:actions>
                      <Button
                        @intent="primary"
                        @appearance="minimal"
                        {{on "click" (fn this.openEditPeriodDrawer period)}}
                        data-test="btn-edit-period-{{index}}"
                        aria-label={{t "common.edit"}}
                      >
                        <PhPencil @size={{18}} />
                      </Button>
                      <Button
                        @intent="danger"
                        @appearance="minimal"
                        {{on "click" (fn this.removePastPeriod period.id)}}
                        data-test="btn-remove-period-{{index}}"
                        aria-label={{t "common.remove"}}
                      >
                        <PhTrash @size={{18}} />
                      </Button>
                    </:actions>
                    <:body>
                      <VStack class="gap-4">
                        <Text @intent="muted" @tag="span" class="text-sm">
                          {{safeFormatDate
                            period.employmentStartDate
                            month="long"
                            day="2-digit"
                            year="numeric"
                          }}
                          -
                          {{safeFormatDate
                            period.employmentEndDate
                            month="long"
                            day="2-digit"
                            year="numeric"
                          }}
                        </Text>
                        <Text @intent="muted" @tag="span" class="text-sm">
                          {{period.employmentHours}}
                          {{t "pslf.start_new.hours_per_week"}}
                        </Text>
                      </VStack>
                    </:body>
                  </Section>
                {{/each}}
              </VStack>
            </:body>
          </Section>
        {{/if}}
        <Section class="border-0">
          <:header>{{t "pslf.start_new.sign_your_pslf_form"}}</:header>
          <:body>
            <VStack>
              <div class="my-2">
                <span class="text-danger font-semibold mr-1">*</span>{{t
                  "pslf.start_new.required_items"
                }}
              </div>
              <p class="font-semibold -mb-2">{{t "signature"}}
                <span class="text-danger">*</span>
              </p>
              <div class="border rounded-2xl p-4 max-w-md">
                {{#if this.hasExistingSignature}}
                  <VStack class="items-center">
                    <VStack class="items-center text-teal-600">
                      <PhCheck @size="20" />
                      <span class="font-semibold">
                        {{#if this.signaturePoints.length}}
                          {{t "pslf.start_new.signature_added"}}
                        {{else}}
                          {{t "pslf.start_new.saved_signature"}}
                        {{/if}}
                      </span>
                    </VStack>
                    <img
                      src={{@employee.pslfProfile.signature}}
                      alt={{t "signature_preview"}}
                      class="max-w-md max-h-32"
                      data-test="signature-preview"
                    />
                    <Button
                      @intent="primary"
                      @appearance="outlined"
                      {{on "click" this.openSignatureModal}}
                      data-test="btn-change-signature"
                    >
                      {{t "pslf.sign.update_signature"}}
                    </Button>
                  </VStack>
                {{else}}
                  <VStack class="items-center">
                    <PhPencilSimple @size="32" />
                    <p class="text-gray-600 font-medium">{{t "pslf.start_new.no_signature_yet"}}</p>
                    <Button
                      @intent="primary"
                      @appearance="outlined"
                      {{on "click" this.openSignatureModal}}
                      data-test="btn-add-signature"
                    >
                      {{t "pslf.start_new.add_signature"}}
                    </Button>
                  </VStack>
                {{/if}}
              </div>
              {{#if this.signatureFieldErrors}}
                <div class="text-red-600" data-test="signature-field-errors">
                  {{#each this.signatureFieldErrors as |error|}}
                    <p>{{error}}</p>
                  {{/each}}
                </div>
              {{/if}}
            </VStack>
          </:body>
        </Section>
        <div class="text-center">
          <Button
            @intent="primary"
            @isRunning={{this.onSubmit.isRunning}}
            @type="submit"
            data-test="btn-submit-form-v2"
          >
            {{t "common.submit"}}
          </Button>
        </div>
      </VStack>
    </Form>
    <Modal
      @isOpen={{this.isOpenSignatureModal}}
      @onClose={{this.closeSignatureModal}}
      class="min-h-64"
      @backdrop="faded"
      @size="xl"
      as |m|
    >
      <m.Header>{{t "pslf.start_new.sign_your_pslf_form"}}</m.Header>
      <m.Body>
        <SignaturePad
          @onSignatureUpdate={{this.onSignatureUpdate}}
          @errors={{this.signatureFieldErrors}}
        />
      </m.Body>
      <m.Footer>
        <Footer
          @isRunning={{false}}
          @onCancel={{this.closeSignatureModal}}
          @onSubmit={{this.captureSignature}}
          @submitText={{t "sign"}}
        />
      </m.Footer>
    </Modal>
    <Drawer
      @isOpen={{this.isOpenPastPeriodDrawer}}
      @onClose={{this.closePastPeriodDrawer}}
      @allowCloseButton={{true}}
      @closeOnOutsideClick={{false}}
      as |d|
    >
      <d.Header>
        <VStack>
          <span class="text-xl font-semibold">
            {{if
              this.isEditingExistingPeriod
              (t "pslf.start_new.edit_past_period_drawer_title")
              (t "pslf.start_new.add_past_period_drawer_title")
            }}
          </span>
          <Text @intent="muted" @tag="p" class="text-sm">
            {{t "pslf.start_new.add_past_period_drawer_description"}}
          </Text>
        </VStack>
      </d.Header>
      <d.Body class="pt-0">
        <Form
          @data={{this.drawerFormData}}
          @onChange={{this.handleDrawerFormChange}}
          @onSubmit={{this.handleDrawerFormSubmit}}
          as |form|
        >
          <VStack class="pt-0">
            <Section
              class="p-4 transition-colors
                {{if
                  this.editingPeriodUnknownFein
                  'opacity-60'
                  (if this.editingPeriodGroup 'border-purple-300 bg-purple-50' '')
                }}"
              data-test="drawer-option-select-group"
            >
              <Select
                @name="drawerPastPeriodGroup"
                @label={{t "pslf.start_new.select_employer"}}
                @items={{this.groupOptions}}
                @onAction={{this.selectDrawerGroup}}
                @selectedKey={{if this.editingPeriodGroup this.editingPeriodGroup.id ""}}
                @isDisabled={{this.editingPeriodUnknownFein}}
                @errors={{this.editingPeriodGroupError}}
                data-test="drawer-select-group"
              />
            </Section>
            <div class="flex items-center gap-3 my-1">
              <Divider class="flex-1" />
              <Text @intent="muted" class="text-sm font-semibold uppercase">
                {{t "or"}}
              </Text>
              <Divider class="flex-1" />
            </div>
            <Section
              class="p-4 transition-colors
                {{if
                  this.editingPeriodUnknownFein
                  'border-purple-300 bg-purple-50'
                  (if this.editingPeriodGroup 'opacity-60' '')
                }}"
              data-test="drawer-option-unknown-fein"
            >
              <VStack>
                <Checkbox
                  @name="unknownFein"
                  @label={{t "pslf.start_new.unknown_employer_fein"}}
                  @checked={{this.editingPeriodUnknownFein}}
                  {{on "change" this.toggleUnknownFein}}
                  data-test="drawer-checkbox-unknown-fein"
                />
                {{#if this.editingPeriodUnknownFein}}
                  <Input
                    @name="manualEmployerName"
                    @label={{t "pslf.start_new.employer_name_for_reference"}}
                    @value={{this.editingPeriodManualEmployerName}}
                    @onInput={{this.setManualEmployerName}}
                    data-test="drawer-input-manual-employer-name"
                  />
                {{/if}}
              </VStack>
            </Section>
            <Section>
              <VStack>
                <Text @intent="muted" class="text-sm italic">
                  {{t "pslf.start_new.past_period_dates_hint"}}
                </Text>
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {{t "pslf.start_new.employment_start_date"}}
                    <span class="text-red-500">*</span>
                  </label>
                  <div class="relative">
                    {{! @glint-expect-error: EmberFlatpickr types }}
                    <EmberFlatpickr
                      @date={{or this.drawerFormData.employmentStartDate ""}}
                      @onChange={{this.handleDrawerStartDateChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      @maxDate="today"
                      placeholder={{t "select_date"}}
                      class="tio-form-input
                        {{if this.drawerFormErrors.employmentStartDate 'border-red-500'}}"
                      data-test="drawer-input-start-date"
                    />
                  </div>
                  {{#each this.drawerFormErrors.employmentStartDate as |error|}}
                    <span class="text-sm text-red-500">{{error}}</span>
                  {{/each}}
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {{t "pslf.start_new.employment_end_date"}}
                    <span class="text-red-500">*</span>
                  </label>
                  <div class="relative">
                    {{! @glint-expect-error: EmberFlatpickr types }}
                    <EmberFlatpickr
                      @date={{or this.drawerFormData.employmentEndDate ""}}
                      @onChange={{this.handleDrawerEndDateChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      @minDate={{or this.drawerFormData.employmentStartDate ""}}
                      @maxDate="today"
                      placeholder={{t "select_date"}}
                      class="tio-form-input
                        {{if this.drawerFormErrors.employmentEndDate 'border-red-500'}}"
                      data-test="drawer-input-end-date"
                    />
                  </div>
                  {{#each this.drawerFormErrors.employmentEndDate as |error|}}
                    <span class="text-sm text-red-500">{{error}}</span>
                  {{/each}}
                </div>
                <form.Field @name="employmentHours" as |field|>
                  <field.Input
                    @label={{t "pslf.start_new.employment_hours"}}
                    @type="number"
                    @isRequired={{true}}
                    @errors={{this.drawerFormErrors.employmentHours}}
                    data-test="drawer-input-hours"
                  />
                </form.Field>
              </VStack>
            </Section>
          </VStack>
          <HStack class="gap-2 justify-end mt-6">
            <Button
              @intent="default"
              @appearance="outlined"
              {{on "click" this.closePastPeriodDrawer}}
              data-test="drawer-btn-cancel"
            >
              {{t "common.cancel"}}
            </Button>
            <Button @intent="primary" @type="submit" data-test="drawer-btn-save">
              {{t "common.save"}}
            </Button>
          </HStack>
        </Form>
      </d.Body>
    </Drawer>
  </template>
}
